<template>
  <v-dialog v-model="open" :max-width="1000" @click:outside="undo">
    <template v-slot:activator="{ on }">
      <v-btn color="primary" fab large :disabled="!users" v-on="on" @click="reset">
        <v-icon x-large>mdi-plus</v-icon>
      </v-btn>
    </template>
    <ValidationObserver ref="task" v-slot="{ invalid, validated, passes }">
      <v-card>
        <v-card-title>
          <span class="headline">Task</span>
        </v-card-title>
        <v-card-text>
          <v-container>
            <v-row>
              <v-col>
                <v-row>
                  <v-col cols="12" sm="6" md="4">
                    <ValidationProvider v-slot="{ errors }" name="title">
                      <v-text-field 
                        v-model="task.name" 
                        :error-messages="errors" 
                        label="Task Title*"
                      />
                    </ValidationProvider>
                  </v-col>
                  <div class="flex-grow-1"></div>
                  <v-col cols="12" sm="6" md="5">
                    <ValidationProvider v-slot="{ errors }" name="Assignee" rules="required|email">
                      <v-autocomplete 
                        v-model="task.assigned_to"
                        label="Assignee*"
                        :items="orgUsers"
                        item-text="name"
                        item-value="email"
                        :error-messages="errors"
                        required
                      >
                      </v-autocomplete>
                    </ValidationProvider>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col cols="12" sm="6" md="4">
                    <v-select  
                      v-model="task.task_type" 
                      :items="actions" 
                      label="Action*" 
                    />
                  </v-col>
                  <div class="flex-grow-1"></div>
                  <v-col cols="12" sm="6" md="5">
                    <v-text-field v-model="task.performed_by" label="Performed By"></v-text-field>
                  </v-col>
                </v-row>
                <v-row align="end">
                  <v-col cols="12" sm="6" md="6">
                    <v-textarea v-model="task.extra_install_instructions" label="Note" rows="2"></v-textarea>
                  </v-col>
                  <div class="flex-grow-1"></div>
                  <v-col cols="12" sm="6" md="5">
                    <v-dialog ref="dialog" v-model="modal" :return-value.sync="task.due_on" persistent width="290px">
                      <template v-slot:activator="{ on }">
                        <v-text-field v-model="date" label="Due Date" prepend-icon="mdi-calendar" readonly v-on="on"></v-text-field>
                      </template>
                      <ValidationProvider v-slot="{ errors }" name="Assignee" rules="required">
                        <v-date-picker 
                          v-model="task.due_on" 
                          scrollable 
                          :error-messages="errors">
                          <div class="flex-grow-1"></div>
                          <v-btn text color="primary" @click="undo">Cancel</v-btn>
                          <v-btn text color="primary" @click="$refs.dialog.save(task.due_on)">OK</v-btn>
                        </v-date-picker>
                      </ValidationProvider>
                    </v-dialog>
                  </v-col>
                </v-row>

                <v-row>
                  <v-col cols="6">
                    <v-text-field  
                    :value="deviceModel"
                    label="Device Model"
                    disabled
                  />
                  </v-col>
                </v-row>

              </v-col>
              <v-col style="flex-grow:0;">
                <v-card class="mx-auto pa-2" width="270" height="100%" outlined>
                  <v-subheader>
                    <span class="target-header">Target Devices</span>
                    <v-icon color="blue" style="cursor:pointer;" @click="transfer = true">mdi-circle-edit-outline</v-icon>
                  </v-subheader>
                  <v-simple-table v-if="targetOrg != 'Unassigned'" style="max-height:400px;overflow:auto;">
                    <template v-slot:default>
                      <thead>
                        <tr>
                          <th class="text-left">Vehicles</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="item in selectedVehicles" :key="item.id">
                          <td>{{ item.vehicle_name }}</td>
                        </tr>
                      </tbody>
                    </template>
                  </v-simple-table>
                  <v-data-table 
                    v-else
                    :items="selectedVehicles"
                    :headers="headers"
                    disable-pagination
                    :hide-default-footer="true"
                    item-key="id"
                  />
                </v-card>
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>
        <v-card-actions>
          <div class="flex-grow-1"></div>
          <v-btn color="blue darken-1" text @click="exit">Cancel</v-btn>
          <v-btn color="blue darken-1" :disabled="invalid || !validated" text @click="passes(submit)">Save</v-btn>
        </v-card-actions>
        <ValidationProvider 
          v-if="hasConfig"
          name="title" 
          rules="required"
        >
          <DeviceSelector 
            v-model="selectedVehicles" 
            :table-headers="headers"
            :open="transfer" 
            :device-model="deviceModel"
            :compatibility-limit="compatibilityLimit"
            :compatibility-bounds="compatibilityBounds"
            :firmware-version="firmwareVersion"
            :is-compatible="isCompatible"
            :supports-partial-config="supportsPartialConfig"
            :config-options-version-map="configOptionsVersionMap"
            @exit="handleClose" 
          />
        </ValidationProvider>
      </v-card>
    </ValidationObserver>
  </v-dialog>
</template>

<script>
import DeviceSelector from "./DeviceSelector"

import { Logger } from "aws-amplify"
const logger = new Logger("Create Config Task")

import moment from "moment"
import { mapGetters, mapState } from "vuex"

var xml2js = require('xml2js');

import {deviceConfigMixin} from '@/mixins/deviceConfigMixin.js';

export default {
  name: "CreateTask",
  components: { DeviceSelector },
  mixins: [deviceConfigMixin],
  props: {
    initDetails: {
      type: Object,
      required: true,
      default: () => {}
    },
    orgUsers: {
      type: Array,
      required: true
    },
    config: {
      type: Object,
      required: true
    },
    isCompatible: {
      required: true,
      type: Boolean
    },
    firmwareVersion: {
      required: true,
      type: String
    },
    compatibilityLimit: {
      required: true,
      type: Object
    },
    compatibilityBounds: {
      required: true,
      type: Object
    },
    configType: {
      type: String,
      required: true
    },
    configOptionsVersionMap: {
      type: Object,
      required: true
    }
  },
  data: () => ({
    task: {},
    mode: "new",
    open: false,
    modal: false,
    selectedPackage: null,
    selectedVehicles: [],
    outsideClick: 0,
    dropped: 0,
    reenter: false,
    transfer: false,
    encodedXML: '',
    targetOrg: '',
    headers: [
      {
        text: 'Install Tag',
        value: 'install_tag',
        align: 'left'
      },
      {
        text: 'IMEI',
        value: 'imei',
        align: 'left'
      },
    ]
  }),
  
  computed: {
    ...mapState(["orgs", "users"]),
    ...mapGetters(["orgId", "orgName"]),
    date() {
      if (!this.task.due_on || this.task.due_on === "None" || this.task.due_on === "Invalid date") return moment().format("DD/MM/YYYY")

      return moment(this.task.due_on).format("DD/MM/YYYY")
    },
    actions() {
      if (this.mode === "new") {
        return [
          { text: "Device Configuration", value: "device_config_job" },
        ]
      }
      return [{ text: "Device Configuration", value: "device_config_job" }]
    },
    deviceModel() {
      return this.config != null ? this.config.device_config.target[0].model[0] : '';
    },
    hasConfig() {
      return this.deviceModel.length > 0 ? true : false;
    },
    devices() {
      let devices = [];

      this.selectedVehicles.forEach(vehicle => {
        vehicle.devices.forEach(device => {
          let deviceID = device.device_id;

          if (!devices.includes(deviceID)) {
            devices.push(deviceID);
          }
        })
      })
      return devices;
    },
    supportsPartialConfig() {
      return this.configType == 'partial' ? true : false;
    }
  },
  watch: {
    initDetails: {
      handler(){
          this.task.name = this.initDetails.name;
          this.task.assigned_to = this.initDetails.assigned_to;
      },
      deep: true 
    }
  },
  mounted() {
    this.handleConfigData();
    this.mapInitData();
  },
  methods: {
    handlecConfigType(val) {
      this.supportsPartialConfig = val;
    },
    handleConfigData() {
      logger.info('called handleConfigData', this.config)
      var builder = new xml2js.Builder();
      

      var xml = builder.buildObject(this.config);
      logger.info(xml)
      this.encodedXML = btoa(xml);
    },
    handleClose(data) {
      this.transfer = false;
      this.targetOrg = data;
    },
    mapInitData() {
      this.task = {
        name: this.initDetails.name,
        description: "",
        assigned_to: "",
        performed_by: "",
        num_devices: "1",
        due_on: "",
        extra_install_instructions: "",
        task_type: "device_config_job"
      };
    },
    async submit() {
      this.open = false
      
      this.task.context = {
        device_config: {
          file_format: 'xml',
          data: this.encodedXML,
        },
        devices: this.devices
      }

      // remove propertys not supported for device config jobs
      delete this.task.num_devices;
      delete this.task.extra_install_instructions;

      this.$emit("task", {
        mode: this.mode,
        task: this.task,
      })

      this.$emit('taskCreated');
    },
    undo() {
      this.modal = false
    },
    editTask(task) {
      logger.debug(task, this.$refs.task)
      this.mode = "edit"
      !task.due_on || task.due_on === "None" || task.due_on === "0000-00-00 00:00:00"
        ? (task.due_on = null)
        : (task.due_on = moment.utc(task.due_on).format())
      // this.task = Object.assign(defaultTask(), cleanNone(task))
      this.open = true
      this.$nextTick(() => {
        this.$refs.task.reset()
        const unwatch = this.$watch(
          "task",
          () => {
            logger.debug("validate")
            this.$refs.task.validate()
            unwatch()
          },
          {
            deep: true,
          }
        )
      })
    },

    reset() {
      logger.info('reset!', this.task, this.initDetails)
      // if (!this.$refs.task) return
      this.mode = "new"
      this.selectedVehicles = []
      this.selectedPackage = null
      requestAnimationFrame(() => {
        logger.info("requestAnimationFrame")
        this.$refs.task.validate()
      })
      // this.task = defaultTask();
    },

    exit() {
      this.open = false
      this.$emit("exit")
    },
    setTarget() {},
  },
}
</script>

<style lang="scss" scoped>
  .target-header {
    padding-right: 1em;
  }

  .config-upload {
    padding: 0.5em;
    border: solid #2196F3 ;
  }
</style>
